<template>
  <base-section
    id="article"
    space="72"
  >
    <v-container
      class="py-0"
      style="max-width: 1160px"
    >
      <base-breadcrumb :items="breadcrumbItems" />
    </v-container>

    <v-container
      class="py-0"
      style="max-width: 1160px"
    >
      <v-row justify="center">
        <v-col
          cols="12"
          md="7"
        >
          <news-card
            class="mb-4"
            prominent
            v-bind="article"
          />

          <!-- <news-share /> -->

          <!-- <news-author v-bind="author" /> -->
        </v-col>

        <v-col
          class="hidden-sm-and-down"
          cols="3"
        >
          <news-search />

          <news-categories />

          <news-recent-articles />

          <news-archives />

          <news-tags />
        </v-col>
      </v-row>
    </v-container>

    <related-items
      type="articles"
      :current-id="$route.params.slug"
    />
  </base-section>
</template>

<script>
  import RelatedItems from '@/components/RelatedItems'
  import { SEO_LANGS } from '@/utils/seo'

  export default {
    name: 'SectionNews',

    components: {
      RelatedItems,
      NewsArchives: () => import('@/components/news/Archives'),
      NewsRecentArticles: () => import('@/components/news/RecentArticles'),
      NewsCard: () => import('@/components/news/Card'),
      NewsCategories: () => import('@/components/news/Categories'),
      NewsSearch: () => import('@/components/news/Search'),
      NewsTags: () => import('@/components/news/Tags'),
    },

    data: () => ({
      tagMap: {},
      categoryMap: {},
      tagLabels: [],
      author: {
        name: 'Yooxplore Team',
        blurb: 'Insights from our team on drones and localization.',
        src: require('@/assets/logo/logo.png'),
      },
      article: {
        icon: 'mdi-text',
        date: '',
        category: 'Blog',
        comments: 0,
        slug: '',
        title: '',
        text: '',
        html: '',
        src: require('@/assets/logo/logo.png'),
      },
    }),

    metaInfo() {
      const title = this.article.title || 'Blog'
      const description = this.article.text || this.article.title || ''
      const keywords = this.tagLabels.join(', ')
      const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: title,
        description,
        datePublished: this.article.date || '',
        image: this.article.src || '',
        keywords,
        author: {
          '@type': 'Organization',
          name: 'Yooxplore',
        },
        publisher: {
          '@type': 'Organization',
          name: 'Yooxplore',
          logo: {
            '@type': 'ImageObject',
            url: 'https://yooxplore.com/img/logo.png',
          },
        },
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': `https://yooxplore.com${this.$route.path}`,
        },
      }
      return {
        title,
        meta: [
          { vmid: 'description', name: 'description', content: description },
          { vmid: 'keywords', name: 'keywords', content: keywords },
        ],
        script: [
          {
            vmid: 'article-jsonld',
            type: 'application/ld+json',
            json: jsonLd,
          },
        ],
      }
    },

    computed: {
      breadcrumbItems() {
        const lang = this.$i18n.locale
        const items = [
          { text: this.$t('nav.home'), to: `/${lang}` },
          { text: this.$t('nav.blog'), to: `/${lang}/blog` },
        ]
        if (this.article.title) {
          items.push({ text: this.article.title })
        }
        return items
      },
      canonicalPath() {
        return this.article.slug ? `/${this.$i18n.locale}/blog/${this.article.slug}` : this.$route.path
      },
    },

    watch: {
      '$route.params.slug'() {
        this.loadArticle()
      },
      '$i18n.locale'() {
        this.loadTaxonomies()
        this.loadArticle()
      },
    },

    async mounted() {
      await this.loadTaxonomies()
      await this.loadArticle()
    },

    methods: {
      getSeoCanonicalPath() {
        return this.canonicalPath
      },
      getSeoAlternatePaths() {
        if (!this.article.slug) return null
        return SEO_LANGS.reduce((acc, lang) => {
          acc[lang] = `/${lang}/blog/${this.article.slug}`
          return acc
        }, {})
      },
      buildMap(list) {
        if (!Array.isArray(list)) return {}
        return list.reduce((acc, item) => {
          if (item && item.dictValue) {
            acc[item.dictValue] = item.dictLabel
          }
          return acc
        }, {})
      },
      async loadTaxonomies() {
        try {
          const [tagResp, categoryResp] = await Promise.all([
            this.$axios({ method: 'get', url: '/public/dict/data', params: { dictType: 'sys_blog_tag' } }),
            this.$axios({ method: 'get', url: '/public/dict/data', params: { dictType: 'sys_blog_category' } }),
          ])
          const tags = Array.isArray(tagResp) ? tagResp : (tagResp?.data || tagResp?.items || [])
          const categories = Array.isArray(categoryResp) ? categoryResp : (categoryResp?.data || categoryResp?.items || [])
          this.tagMap = this.buildMap(tags)
          this.categoryMap = this.buildMap(categories)
        } catch (error) {
          this.tagMap = {}
          this.categoryMap = {}
        }
      },
      mapCategory(value) {
        if (!value) return 'Blog'
        return this.categoryMap[value] || value
      },
      mapTags(value) {
        if (!value) return []
        const tags = value.split(',').map(v => v.trim()).filter(Boolean)
        return tags.map(tag => this.tagMap[tag] || tag)
      },
      async loadArticle() {
        const slug = this.$route.params.slug
        if (!slug) return

        // 优先按 slug 查询；纯数字 ID 则回退旧接口
        const isNumericId = /^\d+$/.test(String(slug))
        const url = isNumericId
          ? `/public/blog/${slug}`
          : `/public/blog/by-slug/${encodeURIComponent(slug)}`

        try {
          const resp = await this.$axios({
            method: 'get',
            url,
            params: { lang: this.$i18n.locale },
          })
          const tagLabels = this.mapTags(resp.tags)
          const articleTitle = resp.title || ''
          // 为富文本中空 ALT 图片补充文章标题（SEO）
          let content = resp.content || resp.excerpt || ''
          if (articleTitle) {
            content = content.replace(/<img([^>]*)\balt=["'](\s*)["']/gi, `<img$1alt="${articleTitle}"`)
          }
          this.article = {
            ...this.article,
            slug: resp.slug || this.article.slug,
            title: articleTitle,
            text: resp.excerpt || '',
            html: content,
            date: resp.publishAt || '',
            category: this.mapCategory(resp.category),
            src: resp.coverImage || this.article.src,
          }
          this.tagLabels = tagLabels

          if (resp.slug && this.$route.params.slug !== resp.slug) {
            this.$router.replace({
              name: 'BlogArticle',
              params: { lang: this.$i18n.locale, slug: resp.slug },
              query: this.$route.query,
            })
          }
        } catch (error) {
          this.$router.replace({ name: 'FourOhFour', params: { lang: this.$i18n.locale } })
        }
      },
    },
  }
</script>
